import React from 'react';
import { BookOpen, Users, Smile, Star, Heart, Award } from 'lucide-react';
import activity1 from '../assets/Activity.jpg';
import activity2 from '../assets/Activity1.jpg';
import activity3 from '../assets/Activiy2.jpg';
import PageHero from './PageHero';

interface StudentCornerProps {
  title?: string;
  description?: string;
  mainImage?: string;
  galleryImages?: string[];
  sections?: {
    title: string;
    content: string;
    icon?: React.ReactNode;
  }[];
  backgroundColor?: string;
  heroGradientColors?: {
    from: string;
    via?: string;
    to: string;
  };
}

const StudentCorner: React.FC<StudentCornerProps> = ({
  title = "Kids Corner",
  description = "One of the most powerful forces that helps in the development of self confidence and perhaps the entire personality is the power of expression both written and oral.",
  mainImage = activity3,
  galleryImages = [
    activity1,
    activity2,
    activity3
  ],
  sections = [
    {
      title: "Primary Development",
      content: "At Primary stage begins the training in listening, speaking, reading and writing and mathematical skill in being able to compare size of objects. Understanding of number value is also developed. Assessment of the growth of the child is evaluated on the basis of the child's work in class. Very much emphasis is given on the Primary wing.",
      icon: <BookOpen className="w-6 h-6" />
    }
  ],
  backgroundColor = "bg-gradient-to-br from-blue-50 to-indigo-100",
  heroGradientColors = {
    from: 'from-purple-600',
    via: 'via-pink-600',
    to: 'to-orange-600'
  }
}) => {
  return (
    <div className={`min-h-screen ${backgroundColor}`}>
      <PageHero
        title={title}
        subtitle={description}
        gradientColors={heroGradientColors}
        titleGradient={{
          from: 'from-white',
          to: 'to-orange-100'
        }}
        subtitleColor="text-orange-100"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Text Content */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-4">
                  {section.icon && (
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                      {section.icon}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Image */}
          <div className="flex items-center justify-center">
            <div className="relative group">
              <img
                src={mainImage}
                alt="Kids Corner Main"
                className="rounded-2xl shadow-2xl w-full h-auto max-w-lg group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {galleryImages.map((image, index) => (
            <div key={index} className="relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img
                src={image}
                alt={`Student activity ${index + 1}`}
                className="w-full h-64 md:h-80 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="flex items-center space-x-2">
                    <Heart className="w-5 h-5 fill-current" />
                    <span className="text-sm font-medium">Learning & Growing</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats/Features Section */}
        <div className="mt-16 bg-white rounded-3xl p-8 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-200">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Interactive Learning</h4>
              <p className="text-gray-600">Engaging activities that promote collaborative learning and social development</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-200">
                <Smile className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Joyful Environment</h4>
              <p className="text-gray-600">Creating a happy and nurturing space where children love to learn and explore</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors duration-200">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Achievement Focus</h4>
              <p className="text-gray-600">Celebrating every milestone and encouraging continuous growth and development</p>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="mt-12 flex justify-center space-x-2">
          <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          <div className="w-3 h-3 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
        </div>
      </div>
    </div>
  );
};

// Example usage with default content
const ExampleUsage = () => {
  const customSections = [
    {
      title: "Primary Development",
      content: "At Primary stage begins the training in listening, speaking, reading and writing and mathematical skill in being able to compare size of objects. Understanding of number value is also developed. Assessment of the growth of the child is evaluated on the basis of the child's work in class. Very much emphasis is given on the Primary wing.",
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      title: "Creative Expression", 
      content: "We encourage students to express themselves through various creative activities including art, music, and storytelling. This helps build their confidence and develops their unique personality traits.",
      icon: <Star className="w-6 h-6" />
    }
  ];

  return (
    <StudentCorner
      title="Kids Corner"
      description="One of the most powerful forces that helps in the development of self confidence and perhaps the entire personality is the power of expression both written and oral."
      sections={customSections}
      galleryImages={[
        activity1,
        activity2
      ]}
    />
  );
};

export default ExampleUsage;